<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="text-center mb-5">
      <h2>{{ $t("PURCHASES_ORDERS.ADD_INVOICE") }}</h2>
    </div>
    <form class="" @submit.prevent="">
      <base-input
        :label="`${$t('PURCHASES_ORDERS.INVOICING_TYPE')} (*)`"
        :placeholder="$t('PURCHASES_ORDERS.INVOICING_TYPE')"
      >
        <el-select
          :name="`${$t('PURCHASES_ORDERS.INVOICING_TYPE')}`"
          :placeholder="$t('PURCHASES_ORDERS.INVOICING_TYPE')"
          v-model="invoicingType"
        >
          <el-option
            :value="INVOICING_TYPE_COMPLETE"
            :label="$t(`PURCHASES_ORDERS.INVOICING_TYPE_COMPLETE`)"
          />
          <el-option
            :value="INVOICING_TYPE_PRODUCT"
            :label="$t(`PURCHASES_ORDERS.INVOICING_TYPE_PRODUCT`)"
          />
          <el-option
            :value="INVOICING_TYPE_AMOUNT"
            :label="$t(`PURCHASES_ORDERS.INVOICING_TYPE_AMOUNT`)"
          />
        </el-select>
      </base-input>

      <div v-if="invoicingType === INVOICING_TYPE_AMOUNT">
        <base-input
          :label="`${$t('PURCHASES_ORDERS.INVOICE_TYPE')} (*)`"
          :placeholder="$t('PURCHASES_ORDERS.INVOICE_TYPE')"
          input-classes="form-control-alternative"
        >
          <el-select
            :name="`${$t('PURCHASES_ORDERS.INVOICE_TYPE')}`"
            :placeholder="$t('PURCHASES_ORDERS.INVOICE_TYPE')"
            v-model="invoicePartialAmountModel.invoice_type"
          >
            <el-option
              :value="INVOICE_TYPE_DOWNPAYMENT"
              :label="$t(`PURCHASES_ORDERS.INVOICE_TYPE_DOWNPAYMENT`)"
            />
            <el-option
              :value="INVOICE_TYPE_STANDARD"
              :label="$t(`PURCHASES_ORDERS.INVOICE_TYPE_STANDARD`)"
            />
          </el-select>
        </base-input>

        <base-input
          :label="$t('COMMON.EXCERPT')"
          :placeholder="$t('COMMON.EXCERPT')"
          input-classes="form-control-alternative"
        >
          <html-editor
            v-model="invoicePartialAmountModel.excerpt"
            @change="onFormChanged()"
          >
          </html-editor>
        </base-input>

        <base-input
          :label="`${$t('COMMON.AMOUNT')} (*)`"
          :placeholder="$t('COMMON.AMOUNT')"
          v-model="invoicePartialAmountModel.amount"
          type="number"
          step="0.0001"
          input-classes="form-control-alternative"
        >
        </base-input>

        <taxes-selector
          v-if="false"
          :label="$t('COMMON.TAXES')"
          :taxes="invoicePartialAmountModel.taxes"
          @taxesChanged="(taxes) => (invoicePartialAmountModel.taxes = taxes)"
        />
        <validation-error :errors="apiValidationErrors.taxes" />
      </div>

      <div v-if="invoicingType === INVOICING_TYPE_PRODUCT">
        <div
          class="row invoice-item"
          v-for="item in purchasesOrder.invoicingItemsState.remainingItems"
          :key="item.id"
        >
          <div class="col-8 invoice-item-name">
            <span class="code text-bold h3">{{ item.code }}</span>
            <span class="name">{{ item.name }}</span>
            <div class="except text-muted" v-html="item.excerpt"></div>
          </div>
          <div class="col-4">
            <base-input
              :label="`${$t('COMMON.QUANTITY')} (*)`"
              :placeholder="$t('COMMON.QUANTITY')"
              v-model="invoicePartialProductModel[item.id].quantity"
              type="number"
              step="1"
              input-classes="form-control-alternative"
            >
            </base-input>
            <validation-error :errors="apiValidationErrors.quantity" />
          </div>
        </div>
      </div>
    </form>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="handleSubmit"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("PURCHASES_ORDERS.ADD_INVOICE") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closePurchasesOrderInvoiceForm"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import {
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import formMixin from "@/mixins/form-mixin";
import {
  INVOICING_TYPE_COMPLETE,
  INVOICING_TYPE_PRODUCT,
  INVOICING_TYPE_AMOUNT,
  INVOICE_TYPE_DOWNPAYMENT,
  INVOICE_TYPE_STANDARD,
} from "@/constants/invoices";
import requestErrorMixin from "@/mixins/request-error-mixin";
import ValidationError from "@/components/ValidationError.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import TaxesSelector from "@/components/TaxesSelector.vue";

export default {
  components: {
    ValidationError,
    HtmlEditor,
    TaxesSelector,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, requestErrorMixin],

  props: ["purchasesOrder", "showModal"],

  data() {
    return {
      invoicePartialProductModel: [],
      invoicePartialAmountModel: {
        invoice_type: INVOICE_TYPE_STANDARD,
        amount: 0,
        excerpt: "",
      },
      invoicingType: INVOICING_TYPE_COMPLETE,
      loading: false,
      INVOICING_TYPE_COMPLETE: INVOICING_TYPE_COMPLETE,
      INVOICING_TYPE_AMOUNT: INVOICING_TYPE_AMOUNT,
      INVOICING_TYPE_PRODUCT: INVOICING_TYPE_PRODUCT,
      INVOICE_TYPE_DOWNPAYMENT: INVOICE_TYPE_DOWNPAYMENT,
      INVOICE_TYPE_STANDARD: INVOICE_TYPE_STANDARD,
      formErrors: null,
    };
  },

  computed: {},

  methods: {
    setInvoicePartialProductModel() {
      if (this.purchasesOrder.invoicingItemsState.remainingItems) {
        const invoiceItemsData = {};
        for (const item of Object.values(
          this.purchasesOrder.invoicingItemsState.remainingItems
        )) {
          invoiceItemsData[item.id] = {
            id: item.id,
            quantity: item.quantity,
          };
        }
        this.invoicePartialProductModel = invoiceItemsData;
        this.invoicingType = this.purchasesOrder.invoicing_type
          ? this.purchasesOrder.invoicing_type
          : INVOICING_TYPE_COMPLETE;
      }
    },

    setInvoicePartialAmountModel() {
      this.invoicePartialAmountModel.amount =
        this.purchasesOrder.invoicingAmountsState.remainingInvoiceAmount;
    },

    closePurchasesOrderInvoiceForm() {
      this.$emit("closePurchasesOrderInvoiceForm", true);
    },

    async handleSubmit() {
      try {
        this.loading = true;
        let invoiceItems = {};
        if (this.invoicingType === INVOICING_TYPE_PRODUCT) {
          invoiceItems = cloneDeep(this.invoicePartialProductModel);
        } else if (this.invoicingType === INVOICING_TYPE_AMOUNT) {
          invoiceItems = cloneDeep(this.invoicePartialAmountModel);
        }

        const purchasesInvoice = await this.$store.dispatch(
          "purchasesOrders/generateInvoice",
          {
            id: this.purchasesOrder.id,
            invoicingType: this.invoicingType,
            items: invoiceItems,
          }
        );
        this.$notify({
          type: "success",
          message: this.$t("PURCHASES_ORDERS.PURCHASES_ORDER_INVOICE_ADDED"),
        });
        this.$router.push(this.$objectViewRoute(purchasesInvoice));
      } catch (error) {
        await this.showRequestError(error);
        this.setApiValidation(error.response.data.errors);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    async generateInvoice() {},
  },

  mounted() {},

  watch: {
    showModal() {
      this.setInvoicePartialProductModel();
      this.setInvoicePartialAmountModel();
    },
    purchasesOrder() {},
  },
};
</script>

<style scoped>
.invoice-item .invoice-item-name {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
